// The invitation email's link (APS-274): /invitations/accept?token=…, behind sign-in (which
// comes back here with the token). It accepts once and opens the organization, replacing this
// page in the history so the token does not stay there. Every refusal says what to do next:
// an unknown link, an expired or used invitation, another address signed in, a full plan.
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate, useSearch } from '@tanstack/react-router'
import { useEffect, useRef } from 'react'
import { ApiError } from '../api/client'
import { meQuery, signOut, useAcceptInvitation } from '../api/queries'
import { EntryHead, EntryNote, EntryPage, EntryWait } from '../components/d'
import { useI18n } from '../i18n'
import { consoleAnalytics } from '../lib/analytics'
import type { InvitationSearch } from './search'

/** The message key for a refused acceptance, by the API's status and code. */
export function refusalKey(error: unknown) {
  if (!(error instanceof ApiError)) return 'invite.failed'
  // Not found, or not even a token (a link cut short): the link is not valid.
  if (error.status === 404 || error.status === 400) return 'invite.invalid'
  if (error.status === 410) return error.code === 'expired' ? 'invite.expired' : 'invite.used'
  if (error.status === 403) return 'invite.otherEmail'
  if (error.status === 402) return 'invite.planLimit'
  return 'invite.failed'
}

export function InvitationAcceptPage() {
  const { t } = useI18n()
  const { token } = useSearch({ strict: false }) as InvitationSearch
  const accept = useAcceptInvitation()
  const me = useQuery(meQuery)
  const navigate = useNavigate()
  const qc = useQueryClient()
  const started = useRef(false)

  useEffect(() => {
    if (!token || started.current) return
    started.current = true
    accept.mutate(token, {
      onSuccess: (r) => {
        if (!r.already_member)
          navigate({ to: '/o/$orgId', params: { orgId: r.org_id }, replace: true })
      },
    })
  }, [token, accept.mutate, navigate])

  // Signed in as someone else: out, then in again with the invited address, back to this link.
  async function switchAddress() {
    const url = await signOut().catch(() => null)
    qc.clear()
    consoleAnalytics()?.reset()
    if (url) window.location.assign(url)
    else navigate({ to: '/sign-in', search: { return_to: `/invitations/accept?token=${token}` } })
  }

  const email = me.data?.user.email ?? ''
  if (
    token &&
    (accept.isIdle || accept.isPending || (accept.isSuccess && !accept.data.already_member))
  )
    return (
      <EntryPage>
        <EntryHead kicker={t('invite.kicker')} title={t('invite.joining')} />
        <EntryWait>{t('invite.accepting', { email })}</EntryWait>
      </EntryPage>
    )

  if (accept.isSuccess) {
    const org = me.data?.orgs.find((o) => o.id === accept.data.org_id)
    return (
      <EntryPage>
        <EntryHead
          kicker={t('invite.kicker')}
          title={t('invite.alreadyTitle')}
          lede={t('invite.alreadyBody', { name: org?.name ?? '' })}
        />
        <div className="en-acts">
          <Link
            className="btn solid en-full"
            to="/o/$orgId"
            params={{ orgId: accept.data.org_id }}
            replace
          >
            {t('invite.open')}
          </Link>
        </div>
      </EntryPage>
    )
  }

  const key = token ? refusalKey(accept.error) : 'invite.invalid'
  return (
    <EntryPage>
      <EntryHead kicker={t('invite.kicker')} title={t('invite.refusedTitle')} />
      {/* A failure of the moment is a warning; the rest refuse this link for good. */}
      <EntryNote tone={key === 'invite.failed' ? 'warn' : 'crash'}>{t(key, { email })}</EntryNote>
      <div className="en-acts">
        {key === 'invite.otherEmail' ? (
          <button type="button" className="btn solid en-full" onClick={switchAddress}>
            {t('invite.signOut')}
          </button>
        ) : null}
        <Link className="btn en-full" to="/">
          {t('invite.home')}
        </Link>
      </div>
    </EntryPage>
  )
}
